// ChannelPlanAlignmentDrilldown.jsx
import React, { useMemo, useState, useCallback } from "react";
import { LicenseManager } from "ag-charts-enterprise";
import "ag-charts-community";
import "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";
import { OmniTooltipNextComponent, OmniIconComponent } from "../../../../../shared";

import { AGGRID_LICENCE_KEY } from "../../../../../shared";
LicenseManager.setLicenseKey(AGGRID_LICENCE_KEY);

const asNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

// Mock data with multiple channels


// Helper to build a lookup map from orderedData by channel name
const buildOrderedDataMap = (orderedData) => {
    const orderDataChannel = orderedData?.parent_campaign_details?.channels
    const map = new Map();
    if (!orderDataChannel || !Array.isArray(orderDataChannel)) return map;
    
    orderDataChannel.forEach((item) => {
        const channelName = item.channel_name || item.channel || item.name || 'Unknown Channel';
        const orderedValue = asNumber(
            item.ordered_total ||
            item.ordered_total_local ||
            0
        );
        
        // Build nested map for publishers/suppliers
        const children = item.publishers || item.suppliers || item.nestedData || [];
        const childrenMap = new Map();
        children.forEach((child) => {
            const childName = child.supplier || child.publisher_name || child.name || 'Unknown';
            const childOrdered = asNumber(
                child.ordered_total ||
                child.ordered_total_local ||
                0
            );
            childrenMap.set(childName, childOrdered);
        });
        
        map.set(channelName, { ordered: orderedValue, children: childrenMap });
    });
    
    return map;
};

// Transform channels data to chart format with expand/collapse support
const buildChartData = (channelsData, expandedChannels, orderedData) => {
    if (!channelsData || !Array.isArray(channelsData) || channelsData.length === 0) return [];
    
    const result = [];
    
    // Build lookup map from orderedData for green bars
    const orderedMap = buildOrderedDataMap(orderedData);
    
    // Track channel name occurrences for duplicates
    const channelNameCount = {};
    
    channelsData.forEach((channelData, channelIndex) => {
        const baseChannelName = channelData.channel_name || channelData.channel || 'Unknown Channel';
        
        // Track duplicates and create unique display name
        channelNameCount[baseChannelName] = (channelNameCount[baseChannelName] || 0) + 1;
        const occurrence = channelNameCount[baseChannelName];
        const channelName = occurrence > 1 ? `${baseChannelName} (${occurrence})` : baseChannelName;
        const channelKey = `${baseChannelName}_${channelIndex}`;
        
        // Support multiple property names for children (publishers, suppliers, nestedData)
        const children = channelData.publishers || channelData.suppliers || channelData.nestedData || [];
        const supplierChildren = children.filter(c => c.publisher_type === 'Supplier' || !c.publisher_type);
        const hasChildren = supplierChildren && supplierChildren.length > 0;
        const isExpanded = expandedChannels.has(channelKey);
        
        // Get planned value from channelsData (grey bar)
        const planned = asNumber(
            channelData.channel_total_budget || 
            channelData.planned_total || 
            channelData.totalBudget || 
            channelData.budget ||
            channelData.planned || 
            0
        );
        
        // Get ordered value from orderedData map (green bar), default to 0 if not found
        const orderedInfo = orderedMap.get(baseChannelName);
        const ordered = orderedInfo ? orderedInfo.ordered : 0;
        
        // Add the parent channel row
        result.push({
            name: channelName,
            channelKey: channelKey,
            baseChannelName: baseChannelName,
            planned,
            ordered,
            hasChildren,
            isExpanded,
            isChild: false,
            channelIndex,
        });
        
        // If expanded, add children
        if (isExpanded && hasChildren) {

            // Merge supplierChildren (from channelsData) with orderedInfo?.children (from orderedData)
            const plannedChildrenMap = new Map();
            supplierChildren.forEach((sup) => {
                const supName = sup.publisher_name || sup.supplier || sup.name || 'Unknown';
                plannedChildrenMap.set(supName, sup);
            });
            
            // Get all unique child names from both sources
            const orderedChildrenMap = orderedInfo?.children || new Map();
            const allChildNames = new Set([
                ...plannedChildrenMap.keys(),
                ...orderedChildrenMap.keys()
            ]);
            
            // Iterate over merged children
            allChildNames.forEach((supName) => {
                const sup = plannedChildrenMap.get(supName) || {};
                
                // Get planned value from channelsData children (grey bar), default to 0 if missing
                const supPlanned = asNumber(
                    sup.publisher_budget ||
                    sup.channel_total_budget || 
                    sup.planned_total || 
                    sup.totalBudget || 
                    sup.budget ||
                    sup.planned || 
                    0
                );
                
                // Get ordered value from orderedData children map (green bar), default to 0 if not found
                const supOrdered = orderedChildrenMap.get(supName) || 0;
                
                result.push({
                    name: `  ${supName}`,
                    channelKey: channelKey,
                    baseChannelName: baseChannelName,
                    planned: supPlanned,
                    ordered: supOrdered,
                    hasChildren: false,
                    isChild: true,
                    parentChannel: channelKey,
                });
            });
        }
    });
    
    return result;
};

const ChannelPlanAlignmentDrilldown = ({ channelsData,orderedData }) => {
    // Debug: Log incoming data to understand structure
    
    // Use mock data if channelsData is empty or invalid
    const validChannelsData = useMemo(() => {
        return channelsData;
    }, [channelsData]);

    const [expandedChannels, setExpandedChannels] = useState(new Set());

    const toggleChannel = useCallback((channelKey) => {
        setExpandedChannels((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(channelKey)) {
                newSet.delete(channelKey);
            } else {
                newSet.add(channelKey);
            }
            return newSet;
        });
    }, []);

    const chartData = useMemo(() => {
        return buildChartData(validChannelsData, expandedChannels,orderedData);
    }, [validChannelsData, expandedChannels, orderedData]);

    const options = useMemo(() => {
        return {
            legend: { enabled: false },
            series: [
                {
                    type: "bar",
                    xKey: "name",
                    yKey: "planned",
                    yName: "Planned (PlanIt)",
                    grouped: true,
                    direction: 'horizontal',
                    fill: "#D1D5DB",
                    stroke: "#D1D5DB",
                    cornerRadius: 4,
                    label: { enabled: false },
                },
                {
                    type: "bar",
                    xKey: "name",
                    yKey: "ordered",
                    yName: "Ordered",
                    grouped: true,
                    direction: 'horizontal',
                    fill: "#2DD4BF",
                    stroke: "#2DD4BF",
                    cornerRadius: 4,
                    label: { enabled: false },
                },
            ],
            data: chartData,
            padding: { top: 0, right: 24, bottom: 0, left: 0 },
            axes: [
                {
                    type: "category",
                    position: "left",
                    line: { enabled: false },
                    tick: { enabled: false },
                    label: { enabled: false },
                    gridLine: { style: [{ stroke: 'transparent' }] },
                },
                {
                    type: "number",
                    position: "bottom",
                    line: { enabled: false },
                    label: { enabled: false },
                    gridLine: { style: [{ stroke: '#E5E7EB', lineDash: [4, 4] }] },
                },
            ],
        };
    }, [chartData]);

    // Calculate max value for x-axis
    const maxValue = useMemo(() => {
        const allValues = chartData.flatMap(d => [d.planned || 0, d.ordered || 0]);
        return Math.max(...allValues, 0);
    }, [chartData]);

    // Format axis label
    const formatAxisValue = (val) => {
        if (val >= 1000000) return `${(val / 1000000).toFixed(0)}M`;
        if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
        return val.toString();
    };

    // Generate x-axis ticks
    const xAxisTicks = useMemo(() => {
        const tickCount = 5;
        const step = maxValue / (tickCount - 1);
        return Array.from({ length: tickCount }, (_, i) => Math.round(i * step));
    }, [maxValue]);

    const rowHeight = 55;
    const chartHeight = Math.max(200, chartData.length * rowHeight);

    // Find channel info for rendering labels
    const getChannelInfo = (channelKey, channelIndex) => {
        const channel = validChannelsData[channelIndex];
        if (!channel) return { hasChildren: false, isExpanded: false };
        const children = channel?.publishers || channel?.suppliers || channel?.nestedData || [];
        const supplierChildren = children.filter(c => c.publisher_type === 'Supplier' || !c.publisher_type);
        return {
            hasChildren: supplierChildren && supplierChildren.length > 0,
            isExpanded: expandedChannels.has(channelKey)
        };
    };

    return (
        <div className="channel-plan-alignment-container">
            {/* Legend at the top */}
            <div className="channel-plan-legend">
                <div className="legend-item">
                    <span className="legend-color" style={{ background: '#D1D5DB' }}></span>
                    <span className="legend-label">Planned (PlanIt)</span>
                    <OmniTooltipNextComponent style={{ width: "fit-content" }}>
                        <span slot="invoker" className="legend-info">
                            <OmniIconComponent className="is-size-3" icon-id="omni:informative:info" />
                        </span>
                        <div slot="content">Planned budget from PlanIt</div>
                    </OmniTooltipNextComponent>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ background: '#2DD4BF' }}></span>
                    <span className="legend-label">Ordered</span>
                    {/* <OmniIconComponent className="is-size-3 prisma-icon" icon-id="omni:brand:prisma" /> */}
                    <OmniTooltipNextComponent style={{ width: "fit-content" }}>
                        <span slot="invoker" className="legend-info">
                            <OmniIconComponent className="is-size-3" icon-id="omni:informative:info" />
                        </span>
                        <div slot="content">Ordered budget synced to Prisma</div>
                    </OmniTooltipNextComponent>
                </div>
            </div>

            {/* Scrollable chart area */}
            <div className="channel-plan-scroll-container">
                <div className="channel-plan-chart-wrapper" style={{ height: `${chartHeight}px` }}>
                    <div className="channel-labels">
                        {chartData.map((item, index) => {
                            const info = !item.isChild ? getChannelInfo(item.channelKey, item.channelIndex) : null;
                            return (
                                <div 
                                    key={`${item.channelKey}-${index}`}
                                    className={`channel-label-row ${item.isChild ? 'child-row' : 'parent-row'} ${info?.hasChildren ? 'clickable' : ''}`}
                                    onClick={() => !item.isChild && info?.hasChildren && toggleChannel(item.channelKey)}
                                >
                                    <span className="label-text">
                                        {item.isChild ? item.name.trim() : item.name}
                                    </span>
                                    {!item.isChild && info?.hasChildren && (
                                        <span className="expand-icon">{info?.isExpanded ? '∨' : '>'}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="channel-chart" style={{ height: `${chartHeight}px` }}>
                        <AgCharts options={options} style={{ width: '100%', height: `${chartHeight}px` }} />
                    </div>
                </div>
            </div>

            {/* Sticky X-Axis at bottom */}
            <div className="channel-plan-xaxis">
                <div className="xaxis-spacer"></div>
                <div className="xaxis-labels">
                    {xAxisTicks.map((tick, index) => (
                        <span key={index} className="xaxis-tick">{formatAxisValue(tick)}</span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ChannelPlanAlignmentDrilldown;